import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { contactService } from '../../../services/contactService';
import { cn } from '../../../utils/cn';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    preferredContact: 'whatsapp'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const serviceOptions = [
    "General Inquiry",
    "Rejuvenation & Pain Relief Therapies",
    "Stress & Anxiety Relief",
    "Panchakarma, Anti-aging & Detoxification Therapies",
    "Spine & Disc Care Consultation",
    "Skin Care Consultation",
    "Auto-Immune Disease Therapies",
    "Metabolic & Endocrine Disorder Therapies"
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Full name is required';
    }

    if (!formData.email?.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.service) {
      errors.service = 'Please select a service';
    }

    if (!formData.message?.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitStatus(null);
    setErrorMessage('');

    // Validate form before submission
    if (!validateForm()) {
      setSubmitStatus('error');
      setErrorMessage('Please fix the errors in the form before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('📧 Submitting contact form...');

      // Submit to database
      const { data, error } = await contactService.submitContactForm(formData);

      if (error) {
        // Handle specific error types
        if (error.includes('violates row-level security policy')) {
          throw new Error('Unable to submit form due to security settings. Please contact us directly.');
        } else if (error.includes('network') || error.includes('fetch')) {
          throw new Error('Network error. Please check your internet connection and try again.');
        } else if (error.includes('required')) {
          throw new Error('Please fill in all required fields correctly.');
        } else {
          throw new Error(error);
        }
      }

      console.log('✅ Contact form submitted successfully:', data);

      // Show success message
      setSubmitStatus('success');

      // Clear form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        preferredContact: 'whatsapp',
      });

      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('❌ Error submitting contact form:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'An unexpected error occurred. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
      // Auto-hide status message after 8 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setErrorMessage('');
      }, 8000);
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-headline text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Send Us a Message
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Have questions about our services or want to schedule a consultation? Fill out the form below and we'll get back to you within 2 hours.
          </p>
        </div>

        <div className="glass-card p-8 lg:p-12">
          {submitStatus === 'success' && (
            <div className="mb-8 glass-panel p-4 rounded-lg border border-success/20 bg-success/5 animate-fade-in">
              <div className="flex items-start space-x-3">
                <Icon name="CheckCircle" size={24} className="text-success flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-success text-lg mb-1">Message sent successfully! ✨</p>
                  <p className="text-sm text-foreground/80 mb-2">
                    Thank you for contacting us! We've received your message and will get back to you within 2 hours during business hours.
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <Icon name="Clock" size={14} className="text-success" />
                    <span>Business Hours: 8:00 AM - 8:00 PM (480 minutes)</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-8 glass-panel p-4 rounded-lg border border-error/20 bg-error/5">
              <div className="flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-error mb-1">Failed to send message</p>
                  <p className="text-sm text-foreground/80">
                    {errorMessage || 'An unexpected error occurred. Please try again or contact us directly.'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    You can also reach us at: <a href="tel:+917842202255" className="text-primary hover:underline">+91 78422 02255</a>
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                name="name"
                value={formData?.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
                error={validationErrors.name}
                className="morph-hover"
              />

              <Input
                label="Email Address"
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
                error={validationErrors.email}
                className="morph-hover"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Phone Number"
                type="tel"
                name="phone"
                value={formData?.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                className="morph-hover"
              />

              <div className="space-y-2">
                <label className={cn(
                  "block text-sm font-medium",
                  validationErrors.service ? "text-destructive" : "text-foreground"
                )}>
                  Service Interest
                  <span className="text-destructive ml-1">*</span>
                </label>
                <select
                  name="service"
                  value={formData?.service}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full px-4 py-3 glass-panel rounded-lg border transition-all duration-300 morph-hover",
                    validationErrors.service
                      ? "border-destructive focus:border-destructive focus:ring-2 focus:ring-destructive/20"
                      : "border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  )}
                  required
                >
                  <option value="">Select a service</option>
                  {serviceOptions?.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                </select>
                {validationErrors.service && (
                  <p className="text-sm text-destructive">{validationErrors.service}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Preferred Contact Method
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="preferredContact"
                    value="email"
                    checked={formData?.preferredContact === 'email'}
                    onChange={handleInputChange}
                    className="text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Email</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="preferredContact"
                    value="phone"
                    checked={formData?.preferredContact === 'phone'}
                    onChange={handleInputChange}
                    className="text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Phone</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="preferredContact"
                    value="whatsapp"
                    checked={formData?.preferredContact === 'whatsapp'}
                    onChange={handleInputChange}
                    className="text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">WhatsApp</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <label className={cn(
                "block text-sm font-medium",
                validationErrors.message ? "text-destructive" : "text-foreground"
              )}>
                Message
                <span className="text-destructive ml-1">*</span>
              </label>
              <textarea
                name="message"
                value={formData?.message}
                onChange={handleInputChange}
                rows={5}
                placeholder="Tell us about your health concerns, preferred appointment times, or any questions you have..."
                className={cn(
                  "w-full px-4 py-3 glass-panel rounded-lg border transition-all duration-300 morph-hover resize-none",
                  validationErrors.message
                    ? "border-destructive focus:border-destructive focus:ring-2 focus:ring-destructive/20"
                    : "border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20"
                )}
                required
              />
              {validationErrors.message && (
                <p className="text-sm text-destructive">{validationErrors.message}</p>
              )}
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
                className="cta-morph bg-primary hover:bg-primary/90 text-white font-cta font-semibold"
                iconName={isSubmitting ? "Loader2" : "Send"}
                iconPosition="left"
                iconSize={20}
              >
                {isSubmitting ? 'Sending Message...' : 'Send Message'}
              </Button>
            </div>

            <div className="text-center pt-4">
              <p className="text-sm text-muted-foreground">
                By submitting this form, you agree to our privacy policy and terms of service.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;